import { useEffect, useMemo, useState } from 'react';
import { Box, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';
import { Header, Row } from '../../../components/Table/TableModels';
import useActiveBanners from '../../../hooks/useActiveBanners/useActiveBanners';
import useHttpError from '../../../hooks/useHttpError/useHttpError';
import useSnackBar from '../../../hooks/useSnackBar/useSnackBar';
import { Banner, BannersListRequest, BannerScope, BannerStatus, BannerType } from '../../../models/BannersModels';
import ClickableIcon from '../../../components/Icons/ClickableIcon';
import EyeIcon from '../../../components/Icons/EyeIcon';
import BannerStatusTag from '../../../components/StatusTag/BannerStatusTag';
import BannerRowOptions from './BannerRowOptions';
import BannersService from '../../../services/Banners/BannersService';

interface UseBannersTableProps {
  query: string;
  statusFilter: BannerStatus | 'ALL';
  activePage: number;
  pageSize: number;
  onPreview: (banner: Banner) => void;
}

function useBannersTable({ query, statusFilter, activePage, pageSize, onPreview }: UseBannersTableProps) {
  const [t] = useTranslation();
  const theme = useTheme();
  const [banners, setBanners] = useState<Banner[]>([]);
  const [totalBanners, setTotalBanners] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const snackBar = useSnackBar();
  const { getHttpReason } = useHttpError();
  const { fetchActiveBanners } = useActiveBanners();

  const bannerTypeLabels: Record<BannerType, string> = {
    [BannerType.PLANNED_CHANGE]: t('pages.bannerOnboarding.bannerType.plannedChange'),
    [BannerType.OUTAGE]: t('pages.bannerOnboarding.bannerType.outage'),
    [BannerType.OTHER]: t('pages.bannerOnboarding.bannerType.other'),
  };

  const scopeLabels: Record<BannerScope, string> = {
    [BannerScope.GLOBAL]: t('pages.bannerOnboarding.scope.global'),
    [BannerScope.MARKETS]: t('pages.bannerOnboarding.scope.markets'),
    [BannerScope.COMPANIES]: t('pages.bannerOnboarding.scope.companies'),
  };

  /**
   * Fetches the list of banners
   */
  const fetchBanners = () => {
    const requestParams: BannersListRequest = {
      page: activePage - 1,
      pageSize,
      sort: 'title',
      sortingType: 'asc',
      title: query.trim() || undefined,
      status: statusFilter === 'ALL' ? undefined : statusFilter,
    };

    BannersService.api.fetchBanners(requestParams).then(
      (response) => {
        setBanners(
          response.data.banners.map((banner) => ({
            id: banner.id,
            status: banner.status,
            createdBy: banner.createdBy ?? '',
            bannerType: banner.bannerType,
            scope: banner.scopeType,
            selectedMarkets: banner.marketIds ?? [],
            selectedCompanies: banner.companyIds ?? [],
            bannerTitle: banner.title,
            timezone: '',
            startDate: banner.startAt,
            endDate: banner.endAt,
            maintenanceTimezone: '',
            maintenanceStart: banner.maintenanceWindow?.startAt ?? '',
            maintenanceEnd: banner.maintenanceWindow?.endAt ?? '',
            messages: banner.translations ?? {},
            dismissible: banner.dismissible,
          })),
        );
        setTotalBanners(response.data.totalElements);
        setIsLoading(false);
      },
      () => {
        snackBar.showSnackBar(t('pages.banners.fetchBannersError'), 'error');
        setIsLoading(false);
      },
    );
  };

  /**
   * Cancel banner
   * @param id
   */
  const cancelBanner = (id: string) => {
    BannersService.api.cancelBanner(id).then(
      () => {
        fetchBanners();
        fetchActiveBanners();
      },
      (res) => {
        const error = getHttpReason(res.response.data, t('pages.banners.cancelBannerError'));
        snackBar.showSnackBar(error, 'error');
      },
    );
  };

  /**
   * Delete banner
   * @param id
   */
  const deleteBanner = (id: string) => {
    BannersService.api.deleteBanner(id).then(
      () => {
        fetchBanners();
        fetchActiveBanners();
      },
      (res) => {
        const error = getHttpReason(res.response.data, t('pages.banners.deleteBannerError'));
        snackBar.showSnackBar(error, 'error');
      },
    );
  };

  useEffect(() => {
    setIsLoading(true);
    fetchBanners();
  }, [activePage, pageSize, query, statusFilter]);

  const headers: Header[] = useMemo(
    () => [
      { id: 'name', label: t('pages.banners.table.headers.name'), isSortable: true },
      { id: 'type', label: t('pages.banners.table.headers.type') },
      { id: 'scope', label: t('pages.banners.table.headers.scope') },
      { id: 'scheduledStart', label: t('pages.banners.table.headers.scheduledStart') },
      { id: 'scheduledEnd', label: t('pages.banners.table.headers.scheduledEnd') },
      { id: 'status', label: t('pages.banners.table.headers.status') },
      { id: 'createdBy', label: t('pages.banners.table.headers.createdBy') },
      {
        id: 'preview',
        label: '',
        sx: {
          position: 'sticky',
          right: '3rem',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            bottom: 0,
            left: 0,
            width: 1,
            boxShadow: '-8px 0px 16px 0px #00000029',
            opacity: 'var(--sticky-actions-shadow, 0)',
            pointerEvents: 'none',
            transition: 'opacity 120ms ease',
          },
        },
      },
      {
        id: 'options',
        label: '',
        sx: {
          position: 'sticky',
          right: 0,
        },
      },
    ],
    [t],
  );

  /**
   * Formats a scheduled date for display, or an em dash when the banner hasn't been scheduled yet (drafts)
   * @param iso
   */
  const formatScheduledDate = (iso: string) => (iso ? DateTime.fromISO(iso).toFormat('dd/MM/yyyy - HH:mm') : '—');

  const rows: Row[] = useMemo(
    () =>
      banners.map((banner, index) => ({
        id: banner.id,
        cells: [
          { value: banner.bannerTitle },
          { value: bannerTypeLabels[banner.bannerType as BannerType] },
          { value: scopeLabels[banner.scope as BannerScope] },
          { value: formatScheduledDate(banner.startDate) },
          { value: formatScheduledDate(banner.endDate) },
          {
            value: <BannerStatusTag status={banner.status} label={t(`pages.banners.table.status.${banner.status}`)} />,
          },
          { value: banner.createdBy },
          {
            value: (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '100%',
                  minWidth: '3.25rem',
                }}
              >
                <ClickableIcon onClick={() => onPreview(banner)}>
                  <EyeIcon />
                </ClickableIcon>
              </Box>
            ),
            sx: {
              position: 'sticky',
              right: '3rem',
              p: 0,
              backgroundColor: index % 2 !== 0 ? theme.palette.monochrome.monoChrome2 : 'white',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                bottom: 0,
                left: 0,
                width: 1,
                boxShadow: '-8px 0px 16px 0px #00000029',
                opacity: 'var(--sticky-actions-shadow, 0)',
                pointerEvents: 'none',
                transition: 'opacity 120ms ease',
              },
            },
          },
          {
            value: (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '100%',
                  minWidth: '3.25rem',
                }}
              >
                <BannerRowOptions banner={banner} cancelBanner={cancelBanner} deleteBanner={deleteBanner} />
              </Box>
            ),
            sx: {
              placeItems: 'center',
              position: 'sticky',
              right: 0,
              p: 0,
              backgroundColor: index % 2 !== 0 ? theme.palette.monochrome.monoChrome2 : 'white',
            },
          },
        ],
      })),
    [banners, onPreview, t, theme.palette.monochrome.monoChrome2],
  );

  return { headers, rows, totalBanners, isLoading };
}

export default useBannersTable;
